<template>
  <div class="card p-4 shadow-sm">
    <div class="mb-4">
      <h5 class="mb-1">Enrollment Signatures</h5>
      <p class="text-muted small mb-0">Draw your signature once, then apply it only to the form you choose below.</p>
    </div>

    <div v-if="!enrollmentId" class="alert alert-warning mb-0">Select an employer before signing enrollment documents.</div>

    <template v-else>
      <div class="row">
        <div class="col-lg-8 mb-3">
          <label class="form-label">Draw Signature</label>
          <canvas
            ref="canvas"
            width="760"
            height="220"
            class="signature-canvas"
            @pointerdown="startDrawing"
            @pointermove="draw"
            @pointerup="stopDrawing"
            @pointercancel="stopDrawing"
            @pointerleave="stopDrawing"
          ></canvas>
          <div class="d-flex gap-2 mt-2">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="clearCanvas">Clear Signature</button>
          </div>
        </div>
        <div class="col-lg-4 mb-3">
          <label class="form-label">Signed By</label>
          <input v-model="signedBy" type="text" class="form-control" />
          <div class="form-check mt-3">
            <input id="signature-consent" v-model="consent" class="form-check-input" type="checkbox" />
            <label for="signature-consent" class="form-check-label small">
              I intend this electronic signature to represent my signature on the specific form I choose to sign.
            </label>
          </div>
        </div>
      </div>

      <div class="table-responsive mt-3">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th>Form</th>
              <th>Requirement</th>
              <th>Status</th>
              <th class="text-end">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="form in forms" :key="form.type">
              <td><strong>{{ form.label }}</strong></td>
              <td><span class="badge" :class="form.required ? 'bg-danger' : 'bg-secondary'">{{ form.required ? 'Required' : 'When applicable' }}</span></td>
              <td>
                <span v-if="signatureMap[form.type]" class="badge bg-success">Signed</span>
                <span v-else class="badge bg-light text-dark border">Not Signed</span>
              </td>
              <td class="text-end">
                <button
                  type="button"
                  class="btn btn-success btn-sm mb-0"
                  :disabled="saving === form.type || !canSign"
                  @click="signForm(form.type)"
                >
                  <span v-if="saving === form.type" class="spinner-border spinner-border-sm"></span>
                  <span v-else>{{ signatureMap[form.type] ? 'Re-sign' : 'Sign' }}</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: 'EnrollmentSignatures',
  props: {
    enrollmentId: { type: [Number, String], default: null },
    enrollmentData: { type: Object, default: null },
  },
  emits: ['enrollment-updated'],
  data() {
    return {
      drawing: false,
      hasInk: false,
      context: null,
      signedBy: '',
      consent: false,
      saving: null,
      forms: [
        { type: 'worker_agreement', label: 'Worker Agreement', required: true },
        { type: 'ds228', label: 'DS-228', required: true },
        { type: 'direct_deposit', label: 'Direct Deposit', required: false },
        { type: 'w4', label: 'Federal W-4', required: false },
        { type: 'de4', label: 'California DE-4', required: false },
        { type: 'i9', label: 'Form I-9', required: false },
      ],
    };
  },
  computed: {
    signatureMap() {
      const map = {};
      const signatures = Array.isArray(this.enrollmentData?.signatures) ? this.enrollmentData.signatures : [];
      signatures.forEach((sig) => { map[sig.document_type] = sig; });
      return map;
    },
    canSign() { return Boolean(this.hasInk && this.consent && this.signedBy.trim()); },
  },
  watch: {
    enrollmentData: {
      immediate: true,
      handler(data) {
        if (!this.signedBy) {
          const employee = data?.employee || {};
          this.signedBy = `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
        }
      },
    },
  },
  mounted() { this.initCanvas(); },
  methods: {
    initCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      this.context = canvas.getContext('2d');
      this.context.lineWidth = 2.2;
      this.context.lineCap = 'round';
      this.context.lineJoin = 'round';
      this.context.strokeStyle = '#111827';
    },
    point(event) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      return {
        x: (event.clientX - rect.left) * (canvas.width / rect.width),
        y: (event.clientY - rect.top) * (canvas.height / rect.height),
      };
    },
    startDrawing(event) {
      if (!this.context) this.initCanvas();
      this.drawing = true;
      const p = this.point(event);
      this.context.beginPath();
      this.context.moveTo(p.x, p.y);
      if (event.currentTarget?.setPointerCapture) event.currentTarget.setPointerCapture(event.pointerId);
    },
    draw(event) {
      if (!this.drawing || !this.context) return;
      const p = this.point(event);
      this.context.lineTo(p.x, p.y);
      this.context.stroke();
      this.hasInk = true;
    },
    stopDrawing() { this.drawing = false; },
    clearCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      if (!this.context) this.initCanvas();
      this.context.clearRect(0, 0, canvas.width, canvas.height);
      this.hasInk = false;
    },
    async signForm(documentType) {
      if (!this.canSign || !this.enrollmentId) return;
      this.saving = documentType;
      try {
        const signatureData = this.$refs.canvas.toDataURL('image/png');
        await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/employee-enrollment/${this.enrollmentId}/signature`,
          {
            document_type: documentType,
            signature_data: signatureData,
            signature_type: 'drawn',
            signed_by: this.signedBy.trim(),
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } }
        );
        this.$emit('enrollment-updated');
        Swal.fire({ icon: 'success', title: 'Signed', text: 'Signature recorded for this form.', timer: 1800, showConfirmButton: false });
      } catch (error) {
        const data = error.response?.data;
        const validation = data?.errors ? Object.values(data.errors).flat().join('\n') : null;
        Swal.fire('Signature Failed', validation || data?.message || 'Could not save the signature.', 'error');
      } finally {
        this.saving = null;
      }
    },
  },
};
</script>

<style scoped>
.signature-canvas {
  width: 100%;
  height: 220px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #fff;
  touch-action: none;
  display: block;
}
</style>
